import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ColDef } from "ag-grid-community";
import Grid from "@/lib/organisms/Grid";
import { CustomCellRendererProps } from "ag-grid-react";
import { decodeErrorResponse } from "@/utils/errors";
import { errorPopup, successPopup } from "@/utils/alerts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { EditRatioDialog } from "@/lib/shadcn/components/edit-apartment-ratio-dialog";
import { dateToString } from "@/utils/periods";
import { utcDateFilterParams } from "@/utils/grid";
import { Convert, ManagementFeeRatio } from "@/models/responses";
import { useActiveApartmentEntities } from "@/store/slices/apartments";
import { fetchOptions } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";

interface ExtendedActionsRendererProps extends CustomCellRendererProps {
    onEdit: (id: number) => void;
}

export const ActionsRenderer: React.FC<ExtendedActionsRendererProps> = (params: ExtendedActionsRendererProps) => {
    return (
        <div className={"text-right"}>
            <button
                type="button"
                aria-label="Edit ratio"
                onClick={function () {
                    return params.onEdit(params.data.id);
                }}
                style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: "inherit",
                }}
                className={"text-info-emphasis"}
            >
                <FontAwesomeIcon icon={faEdit} />
            </button>
        </div>
    );
};

const ManagementFeeRatiosPage: React.FC = () => {
    const apartmentEntities = useActiveApartmentEntities();
    const [ratios, setRatios] = useState<ManagementFeeRatio[]>([]);
    const [apartmentToEdit, setApartmentToEdit] = useState<ManagementFeeRatio | undefined>(undefined);
    const [showEditRatiosModal, setShowEditRatiosModal] = useState(false);
    const gridName = "Latest Management Fee Ratios";

    const editManagementFeeRatio = useCallback(
        (id: number) => {
            const apartmentRatio: ManagementFeeRatio = ratios.find((ratio) => ratio.id === id)!;
            setApartmentToEdit(apartmentRatio);
            setShowEditRatiosModal(true);
        },
        [ratios],
    );

    const { predicates } = useApartmentFilters();

    /**
     * Apply the shared apartment-filter predicates to the ratios list. Each
     * ratio carries `apartmentId`; resolve via `apartmentEntities` and feed
     * every active predicate. Ratios whose apartment is unknown are excluded
     * under any active filter — same semantics as Tier 1.
     */
    const filteredRatios = useMemo(() => {
        if (predicates.length === 0) {
            return ratios;
        }
        return ratios.filter((ratio) => {
            const apartment = apartmentEntities[ratio.apartmentId];
            if (!apartment) {
                return false;
            }
            return predicates.every((predicate) => predicate(apartment));
        });
    }, [ratios, predicates, apartmentEntities]);

    const columnDefinitions: ColDef[] = [
        {
            field: "apartmentId",
            headerName: "Apartment",
            valueGetter: (params) => {
                const apartmentName = apartmentEntities[params.data.apartmentId]?.name;
                if (!apartmentName) {
                    console.log("No apartment name found for id: " + params.data.apartmentId);
                }
                return apartmentName;
            },
            width: 350,
            cellDataType: "text",
            filter: "agTextColumnFilter",
        },
        {
            field: "managementFeeRatio",
            headerName: "Management Fee Ratio",
            valueFormatter: (params) => `${(params.value * 100).toFixed(0)}%`,
            width: 220,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => value.data.managementFeeRatio * 100,
        },
        {
            field: "fromDate",
            headerName: "Effective From",
            cellDataType: "date",
            filter: "agDateColumnFilter",
            filterParams: utcDateFilterParams,
            width: 250,
        },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: ActionsRenderer,
            type: ["rightAligned"],
            cellRendererParams: {
                onEdit: (apartmentId: number) => editManagementFeeRatio(apartmentId),
            },
            filter: false,
            flex: 1,
        },
    ];

    useEffect(() => {
        fetch(`${apiUrl}/api/admin/apartments/management-fee-ratios`, fetchOptions)
            .then((response) => response.text())
            .then((data) => {
                let ratios = Convert.toGetManagementFeeRatiosResponse(data).managementFeeRatios;
                setRatios(ratios);
            })
            .catch((error) =>
                toast({
                    title: "Error",
                    description: `Failed to fetch management fee ratios: ${error.message}`,
                    variant: "destructive",
                }),
            );
    }, []);

    const handleModalSubmitted = useCallback(
        (
            apartmentToEdit: ManagementFeeRatio,
            managementFeeRatio: number,
            month: number,
            year: number,
            existingRatio?: ManagementFeeRatio,
        ) => {
            const apartmentId = apartmentToEdit?.apartmentId;
            const fromDate = new Date(year, month, 1);
            const isUpdate = !!existingRatio;

            const url = isUpdate
                ? `${apiUrl}/api/admin/apartments/${apartmentId}/management-fee-ratios/${existingRatio.id}`
                : `${apiUrl}/api/admin/apartments/management-fee-ratios`;
            const method = isUpdate ? "PUT" : "POST";

            fetch(url, {
                method: method,
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    apartmentId: apartmentId,
                    managementFeeRatio: managementFeeRatio,
                    fromDate: dateToString(fromDate),
                }),
            })
                .then((response) => {
                    if (!response.ok) return decodeErrorResponse(response);
                    return response.text();
                })
                .then((data) => {
                    let updatedManagementFeeRatio: ManagementFeeRatio = Convert.toGetManagementFeeRatioResponse(data).managementFeeRatio;
                    let updatedRatios: ManagementFeeRatio[];

                    if (isUpdate) {
                        // Update existing ratio
                        updatedRatios = ratios.map((ratio: ManagementFeeRatio) =>
                            ratio.id === updatedManagementFeeRatio.id ? updatedManagementFeeRatio : ratio,
                        );
                    } else {
                        // Add new ratio and remove old one for same apartment (if this is the latest)
                        updatedRatios = ratios.filter((ratio: ManagementFeeRatio) => ratio.apartmentId !== apartmentId);
                        updatedRatios.push(updatedManagementFeeRatio);
                    }

                    setRatios(updatedRatios);
                    successPopup(isUpdate ? "Management Fee ratio updated successfully" : "Management Fee ratio created successfully");
                    setShowEditRatiosModal(false);
                })
                .catch((error) => {
                    void errorPopup("Failed to update ratio:" + error.message);
                });
        },
        [ratios],
    );

    const handleModalClosed = useCallback(() => {
        setApartmentToEdit(undefined);
        setShowEditRatiosModal(false);
    }, []);

    return (
        <div data-testid="management-fee-ratios-page" className="flex flex-col gap-2">
            <ApartmentFilterBar />
            <EditRatioDialog
                open={showEditRatiosModal}
                onClose={handleModalClosed}
                onSubmit={(managementFeeRatio, month, year, existingRatio) =>
                    handleModalSubmitted(apartmentToEdit!, managementFeeRatio, month, year, existingRatio as ManagementFeeRatio)
                }
                modalTitle={"Management Fee Ratio"}
                apartmentName={apartmentEntities[apartmentToEdit?.apartmentId || 0]?.name || ""}
                apartmentId={apartmentToEdit?.apartmentId || 0}
                ratio={apartmentToEdit?.managementFeeRatio || 0}
                fromDate={apartmentToEdit?.fromDate || new Date()}
            />
            <Grid<ManagementFeeRatio>
                columnDefs={columnDefinitions}
                gridName={gridName}
                initialRowData={filteredRatios}
                canExport={false}
                getRowId={(params) => params.data.apartmentId.toString()}
            />
        </div>
    );
};
export default ManagementFeeRatiosPage;
